import type { VectorNetwork } from "@/document/model/vectorNetwork";

export interface SculptBrushPoint {
  x: number;
  y: number;
}

export interface SculptBrushOptions {
  radius: number;
  strength: number;
}

export interface SculptedVertex extends SculptBrushPoint {
  vertexId: string;
}

// Sub-step spacing as a fraction of the brush radius. Small enough that a
// grabbed vertex glides with the pointer instead of snapping forward in coarse
// dabs, but still only a handful of steps per pointer event so the stroke stays
// cheap regardless of how fast the pointer moves.
const SEGMENT_STEP_FACTOR = 0.05;
const MIN_SEGMENT_STEP = 0.75;

/** Snapshot of the network's vertices used as the mutable sculpt working set. */
export function initialSculptPositions(network: VectorNetwork): SculptedVertex[] {
  return network.vertices.map(vertex => ({
    vertexId: vertex.id,
    x: vertex.x,
    y: vertex.y,
  }));
}

/**
 * Applies a single brush movement segment (`from` -> `to`) to `positions` in
 * place, dragging any vertex within the brush along with the pointer scaled by
 * strength and a radial falloff. The segment is sub-stepped so fast pointer
 * moves deform as smoothly as slow ones and vertices never jump in big chunks.
 */
export function applySculptSegment(
  positions: SculptedVertex[],
  from: SculptBrushPoint,
  to: SculptBrushPoint,
  options: SculptBrushOptions,
): void {
  const radius = Math.max(1, options.radius);
  const strength = Math.max(0, options.strength) / 100;
  if (strength === 0) {
    return;
  }

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy);
  if (distance === 0) {
    return;
  }

  const spacing = Math.max(MIN_SEGMENT_STEP, radius * SEGMENT_STEP_FACTOR);
  const steps = Math.max(1, Math.ceil(distance / spacing));
  const moveX = dx / steps;
  const moveY = dy / steps;

  for (let step = 1; step <= steps; step += 1) {
    const t = step / steps;
    const centerX = from.x + dx * t;
    const centerY = from.y + dy * t;
    for (const vertex of positions) {
      const vx = vertex.x - centerX;
      const vy = vertex.y - centerY;
      const dist = Math.hypot(vx, vy);
      if (dist >= radius) {
        continue;
      }
      const falloff = (1 - dist / radius) ** 2;
      vertex.x += moveX * strength * falloff;
      vertex.y += moveY * strength * falloff;
    }
  }
}

/**
 * Pure whole-stroke reference: replays a sampled stroke against a fresh copy of
 * the network. Used for deterministic tests; the live tool applies segments
 * incrementally via {@link applySculptSegment} for identical results.
 */
export function sculptVectorVertices(
  network: VectorNetwork,
  samples: SculptBrushPoint[],
  options: SculptBrushOptions,
): SculptedVertex[] {
  const positions = initialSculptPositions(network);
  for (let index = 1; index < samples.length; index += 1) {
    applySculptSegment(positions, samples[index - 1]!, samples[index]!, options);
  }
  return positions;
}
